import { useMutation, useQueryClient } from "@tanstack/react-query"
import React, { Fragment, useEffect } from "react"
import { Controller, useForm } from "react-hook-form"

import { uploadAvatar } from "@/api"
import { BottomSheetModal } from "@/components/shared"
import { Button, Image } from "@/components/ui"
import { EFolderAssets } from "@/modules/helpers/enum"
import { useCurrentUser } from "@/modules/helpers/hooks/shared"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import { getImageByNameFromS3, sendPermissionCamera } from "@/modules/helpers/utils"
import { uploadFileToS3 } from "@/modules/helpers/utils/aws-s3"
interface AvatarUploadModalProps {
  open: boolean
  setIsVisible: (status: boolean) => void
}

interface ChangeImageProps {
  label?: string
  onChange: (image: File) => void
}
const ChangeImage: React.FC<ChangeImageProps> = ({ label = "Thay đổi ảnh", onChange }) => {
  return (
    <label htmlFor="file" className="w-fit">
      <div className="flex w-fit mx-auto active:bg-primary/50 transition-all ease-in-out items-center justify-center px-4 h-9 text-center font-semibold text-white rounded-full bg-primary text-xs">
        {label}
      </div>
      <input
        id="file"
        name="file"
        aria-label="inputfile"
        type="file"
        accept="image/*"
        onClick={() => {
          sendPermissionCamera()
        }}
        onChange={(e) => {
          if (e.target.files) onChange(e.target.files[0])
        }}
        className="opacity-0 w-0 h-0"
      />
    </label>
  )
}

export const AvatarUploadModal: React.FC<AvatarUploadModalProps> = ({ open, setIsVisible }) => {
  const queryClient = useQueryClient()
  const error = useForm({
    defaultValues: {
      update: ""
    }
  })
  const { user } = useCurrentUser()

  const form = useForm<{ img: File | null }>({
    defaultValues: {
      img: null
    }
  })

  const img = form.watch("img")

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      return uploadAvatar(`avatar_${user?.id}_${Date.now()}.png`)
    },
    mutationKey: ["update_avatar", user?.id],
    onSuccess({ data }) {
      const avatarUploadUrl = data.data.avatarUploadUrl
      if (avatarUploadUrl && img) {
        return uploadFileToS3(avatarUploadUrl, img).then(() => {
          form.reset()
          setIsVisible(false)
          return queryClient.invalidateQueries({ queryKey: ["user_info"] })
        })
      }
    },
    onError() {
      error.setError("update", { message: "Có lỗi xảy ra! Vui lòng thử lại sau." })
    }
  })

  const onUploadAvatar = () => {
    if (img) {
      mutate()
    }
  }
  useEffect(() => {
    if (open) {
      error.reset()
      form.reset()
    }
  }, [open, error, form])

  return (
    <BottomSheetModal isVisible={open} setIsVisible={setIsVisible} title="Chỉnh sửa ảnh đại diện">
      <div className="flex flex-col gap-2 p-2 relative">
        <Controller
          name="update"
          control={error.control}
          render={({ fieldState }) => {
            if (!fieldState.error?.message) return <Fragment></Fragment>
            return (
              <div className="w-full rounded-lg px-4 py-1 bg-red-500">
                <p className="text-white text-xs font-normal"> {fieldState.error?.message}</p>
              </div>
            )
          }}
        />
        {!!img && (
          <div className="border py-4 m-auto cursor-pointer rounded-xl flex items-center justify-center w-full h-fit flex-col">
            <ChangeImage
              onChange={(image) => {
                form.setValue("img", image)
              }}
              label="Thay đổi"
            />
            <Image
              src={URL.createObjectURL(img)}
              alt="avatar"
              width={400}
              className="border cursor-pointer rounded-full object-cover flex items-center justify-center w-32 h-32"
              height={400}
            />
          </div>
        )}
        {!!user?.avatarName && !img && (
          <div className="border py-4 m-auto cursor-pointer rounded-xl flex items-center justify-center w-full h-fit flex-col">
            <ChangeImage
              onChange={(image) => {
                form.setValue("img", image)
              }}
              label="Thay đổi"
            />
            <Image
              src={`${
                user?.avatarName?.includes("https")
                  ? user?.avatarName
                  : getImageByNameFromS3(user?.avatarName, user?.id, EFolderAssets.AVATAR)
              }`}
              alt="avatar"
              width={400}
              className="border cursor-pointer rounded-full object-cover flex items-center justify-center w-32 h-32"
              height={400}
            />
          </div>
        )}
        {!user?.avatarName && !img && (
          <div className="flex flex-wrap justify-center items-center py-4 gap-3 overflow-y-auto h-1/2">
            <ChangeImage
              onChange={(image) => {
                form.setValue("img", image)
              }}
              label="Tải hình ảnh lên"
            />
          </div>
        )}

        <Button
          disabled={isPending}
          className={cn("w-full !h-12 rounded-xl mt-2 bg-gradient-primary")}
          onClick={onUploadAvatar}
        >
          Lưu
        </Button>
      </div>
    </BottomSheetModal>
  )
}
